// ----------------------------------------------------------------------
// <copyright file="Izenda.VirtualListUIStore.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, observable, computed, untracked, autorun } from 'mobx';
import { flow, getOr, get, flatMap, filter, findLast, map, reduce, debounce } from 'lodash/fp';

/**
 * Store for virtual list data
 */
export default class VirtualListUIStore {
  pageToScreenRatio = 0;
  numPageOnScreen = 3;
  training = true;

  @observable width = 0;
  @observable startIndex = 0;
  @observable viewportHeight = 0;
  @observable viewportWidth = 0;
  @observable itemHeight = 0;
  @observable itemWidth = 0;

  /**
   * @type {VirtualPage[]}
   */
  @observable pages = [];

  constructor(pageToScreenRatio, items) {
    this.pageToScreenRatio = pageToScreenRatio;
    this.initialize(items, 0);
  }

  @computed get items() {
    return flatMap(page => page.items.slice(), this.pages.slice());
  }

  @computed get screenPageHeight() {
    return this.viewportHeight
      ? this.viewportHeight * this.pageToScreenRatio
      : Infinity;
  }

  @computed get bufferHeight() {
    const startIndex = this.startIndex;
    return flow(
      filter(page => page.id < startIndex),
      map(get('height')),
      reduce((acc, height) => acc + height, 0)
    )(this.pages);
  }

  @computed get virtualItemsOnScreen() {
    const startIndex = this.startIndex;
    const lastIndex = Math.min(this.pages.length, startIndex + this.numPageOnScreen) - 1;
    return flow(
      filter(page => page.id >= startIndex && page.id <= lastIndex),
      flatMap(get('items'))
    )(this.pages);
  }

  /**
   *
   * @param {VirtualItem[]} items
   * @param {function} getChildItems
   */
  @action initialize(items, getChildItems) {
    if (!items || items.length === 0) return;

    let lastPage, itemIndex, itemsLength, item;
    let childIndex, childLength, childItem, childItems;

    if (this.pages.length > 0) {
      lastPage = this.pages[this.startIndex];
    } else {
      lastPage = this.newVirtualPage();
    }

    if (this.training) {
      // Render only one item for training its height
      lastPage.append(new VirtualItem(items[0], true));
      this.training = false;
    } else {
      // Take one item dimension as default item dimension
      if (!this.itemHeight) this.itemHeight = lastPage.items[0].height;
      if (!this.itemWidth) this.itemWidth = lastPage.items[0].width;

      // Clear out training rendered items/pages.
      this.release();

      // Intentionally use for loop that give best performance on large item list.
      for (itemIndex = 0, itemsLength = items.length; itemIndex < itemsLength; itemIndex++) {
        item = items[itemIndex];
        this.append(new VirtualItem(item));

        if (getChildItems) {
          childItems = getChildItems(item);

          // Scan thru child items and append virtual item for child items
          for (childIndex = 0, childLength = childItems.length; childIndex < childLength; childIndex++) {
            childItem = childItems[childIndex];
            this.appendSilent(new VirtualItem(childItem));
          }
        }
      }
    }
  }

  /**
   * Append virtual item to the last page (if current last page not exist or full - create new one)
   * @param {VirtualItem} item
   */
  appendSilent(item) {
    if (!item) return;
    const pages = this.pages;
    let lastPage = pages.length > 0 ? pages[pages.length - 1] : null;

    if (!lastPage || !lastPage.hasVacancy) {
      lastPage = this.newVirtualPage();
    }
    lastPage.appendSilent(item);
  }

  /**
   * Append virtual item to the last page (if current last page not exist or full - create new one)
   * @param {VirtualItem} item
   */
  @action append(item) {
    if (!item) return;
    const pages = this.pages;
    let lastPage = pages.length > 0 ? pages[pages.length - 1] : null;

    if (!lastPage || !lastPage.hasVacancy) {
      lastPage = this.newVirtualPage();
    }
    lastPage.append(item);
  }

  /**
   * Reset all pages
   */
  @action release() {
    this.pages = [];
    VirtualItemsCounter.reset();
  }

  @action setViewportDimension(width, height) {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.repartition();
  }

  calculateStartIndex(scrollTop) {
    let runningTop = 0;
    const current = flow(
      map(page => {
        const pageCoord = {
          id: page.id,
          top: runningTop,
          bottom: runningTop + page.height
        };
        runningTop += page.height;
        return pageCoord;
      }),
      findLast(pageCoord => scrollTop >= pageCoord.top && scrollTop <= pageCoord.bottom)
    )(this.pages);

    // The top page index that is showing in viewport
    const topPageIndex = getOr(0, 'id', current);
    // Max start index
    const maxStartIndex = Math.max(this.pages.length - this.numPageOnScreen, 0);
    // Shift the start page index lower than showing page index
    const startIndex = Math.min(Math.max(topPageIndex - 1, 0), maxStartIndex);
    if (startIndex !== this.startIndex) {
      this.startIndex = startIndex;
    }
  }

  @action repartition() {
    let index, length, itemIndex, pageLength, currPage, currItems, currItem;
    const pages = this.pages.peek();

    this.release();

    let newPage = this.newVirtualPage();
    for (index = 0, length = pages.length; index < length; index++) {
      currPage = pages[index];
      currItems = currPage.items;

      for (itemIndex = 0, pageLength = currItems.length; itemIndex < pageLength; itemIndex++) {
        currItem = currItems[itemIndex];
        if (newPage.hasVacancy) {
          newPage.append(currItem);
        } else {
          newPage = this.newVirtualPage();
          newPage.append(currItem);
        }
      }
    }
  }

  @action
  newVirtualPage() {
    const newPage = new VirtualPage(this);
    this.pages.push(newPage);
    return newPage;
  }
}

/**
 * Page for virtual list
 */
class VirtualPage {
  id = 0;
  /**
   * @type {VirtualListUIStore}
   */
  parent = null;
  /**
   * @type {VirtualItem[]}
   */
  items = [];
  @observable lazyLoaded = false;
  @observable onScreen = false;

  constructor(parent) {
    this.id = parent.pages.length;
    this.parent = parent;
  }

  @computed get top() {
    return 0;
  }

  @computed get bottom() {
    return this.top + this.height;
  }

  @computed get width() {
    return flow(
      map(get('width')),
      reduce((acc, width) => acc + (width === 0 ? this.parent.itemWidth : width), 0)
    )(this.items);
  }

  @computed get height() {
    return flow(
      map(get('height')),
      reduce((acc, height) => acc + (height === 0 ? this.parent.itemHeight : height), 0)
    )(this.items);
  }

  @computed get hasVacancy() {
    return this.height < this.parent.screenPageHeight;
  }

  appendSilent(item) {
    this.items.push(item);
  }

  @action append(item) {
    this.appendSilent(item);
  }
}

/**
 * Virtual list item
 */
class VirtualItem {
  isTrainingMode = false;
  dataItem = null;
  itemIndex = 0;

  @observable top = 0;
  @observable bottom = 0;
  @observable width = 0;
  @observable height = 0;

  constructor(dataItem, isTrainingMode) {
    this.itemIndex = VirtualItemsCounter.generateItemIndex();
    this.dataItem = dataItem;
    this.isTrainingMode = isTrainingMode;
  }

  /**
   * Set up width and height if changed
   * @param {HTMLElement} domElement DOM element
   */
  updateCoordsIfNeeded(domElement) {
    if (!domElement || (this.height === domElement.offsetHeight && this.width === domElement.offsetWidth))
      return false;
    this.updateCoords(domElement);
    return true;
  }

  /**
   * Set up width and height
   * @param {HTMLElement} domElement DOM element
   */
  updateCoords(domElement) {
    if (domElement) {
      if (this.height !== domElement.offsetHeight) {
        this.height = domElement.offsetHeight;
      }
      if (this.width !== domElement.offsetWidth) {
        this.width = domElement.offsetWidth;
      }
    }
  }
}

/**
 * Global counter
 */
class VirtualItemsCounter {
  static itemCount = 0;

  static generateItemIndex() {
    return this.itemCount++;
  }

  static reset() {
    this.itemCount = 0;
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.VirtualListUIStore.js